import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { createTeacherSupportRequest, getClassesBySchoolId, getTeachersBySchoolId, searchTeacherStudents } from "../services/TeacherAPIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse,
  isPlaceholderWorkflowId
} from "../utils/teacherWorkflowRequests";
import { formatClassDisplayName, getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherEscalateTechnicalIssueModal.css";

const issueTypes = [
  { value: "Login or QR code", helper: "Children or teachers cannot get in" },
  { value: "Screener will not load", helper: "A screener or task is blocked" },
  { value: "Export or results issue", helper: "Data is missing or not exporting" },
  { value: "Other issue", helper: "Describe what is happening" }
];

const normaliseText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const normaliseClass = (classItem, countryGrades) => {
  const name = classItem?.name || classItem?.className || "Unnamed class";
  const rawId = getClassId(classItem);
  const grade = getDistinctClassGradeLabel(classItem, name, countryGrades);
  const studentCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.students?.length;

  return {
    id: `class-${rawId}`,
    rawId,
    type: "class",
    name,
    helper: [
      "Class",
      grade,
      Number.isFinite(Number(studentCount)) ? `${Number(studentCount)} children` : ""
    ].filter(Boolean).join(" - "),
    className: name
  };
};

const normaliseTeacher = (teacher) => ({
  id: `teacher-${teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId}`,
  rawId: teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId,
  type: "teacher",
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  helper: teacher?.email || teacher?.roleLabel || "Teacher"
});

const normaliseStudent = (student, countryGrades) => {
  const name =
    student?.name ||
    student?.fullName ||
    [student?.firstName || student?.firstname, student?.lastName || student?.lastname].filter(Boolean).join(" ") ||
    "Unnamed child";
  const className = student?.className || student?.class?.name || student?.classname || "";
  const classDisplayName = formatClassDisplayName(student, className, countryGrades);

  return {
    id: `student-${student?.id || student?._id || student?.studentid || name}`,
    rawId: student?.id || student?._id || student?.studentid || student?.studentId,
    type: "child",
    name,
    helper: `Child${classDisplayName ? ` - ${classDisplayName}` : ""}`,
    className,
    studentName: name
  };
};

function TeacherEscalateTechnicalIssueModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill = null,
  onWorkflowComplete = null,
  onBackToTrialAccess = null
}) {
  const [stage, setStage] = useState("form");
  const [issueType, setIssueType] = useState("");
  const [affectedOptions, setAffectedOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [affectedSearch, setAffectedSearch] = useState("");
  const [selectedAffectedId, setSelectedAffectedId] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [browser, setBrowser] = useState("");
  const [operatingSystem, setOperatingSystem] = useState("");
  const [numberAffected, setNumberAffected] = useState("");
  const [description, setDescription] = useState("");
  const [isLoadingAffected, setIsLoadingAffected] = useState(false);
  const [isSearchingStudents, setIsSearchingStudents] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStage("form");
    const nextClassName = prefill?.className || "";
    const nextStudentName = prefill?.studentName || "";

    setIssueType(prefill?.issueType || "");
    setAffectedOptions([]);
    setStudentOptions([]);
    setAffectedSearch(nextClassName || nextStudentName || prefill?.teacherName || "");
    setSelectedAffectedId("");
    setDeviceType(prefill?.deviceType || "");
    setBrowser(prefill?.browser || "");
    setOperatingSystem(prefill?.operatingSystem || "");
    setNumberAffected(prefill?.numberAffected || "");
    setDescription(prefill?.description || "");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);

    if (!schoolid) {
      return;
    }

    setIsLoadingAffected(true);
    Promise.all([getClassesBySchoolId(schoolid), getTeachersBySchoolId(schoolid)])
      .then(([classesResponse, teachersResponse]) => {
        const classOptions = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, countryGrades))
          .filter((row) => row.id && row.name);
        const teacherOptions = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((row) => row.id && row.name);
        const nextAffectedOptions = [...classOptions, ...teacherOptions];
        const prefilledRecord = nextAffectedOptions.find((row) =>
          (prefill?.classId && row.type === "class" && String(row.rawId) === String(prefill.classId)) ||
          (prefill?.teacherId && row.type === "teacher" && String(row.rawId) === String(prefill.teacherId)) ||
          (prefill?.className && row.type === "class" && normaliseText(row.name) === normaliseText(prefill.className)) ||
          (prefill?.teacherName && row.type === "teacher" && normaliseText(row.name) === normaliseText(prefill.teacherName))
        );

        setAffectedOptions(nextAffectedOptions);
        if (prefilledRecord) setSelectedAffectedId(prefilledRecord.id);
      })
      .catch((loadError) => {
        console.error("Error loading affected technical issue options:", loadError);
        setError("Classes and teachers could not be loaded. You can still describe the issue.");
      })
      .finally(() => {
        setIsLoadingAffected(false);
      });
  }, [show, schoolid, countryGrades, prefill]);

  useEffect(() => {
    const query = affectedSearch.trim();

    if (!show || query.length < 2) {
      setStudentOptions([]);
      return;
    }

    let cancelled = false;
    setIsSearchingStudents(true);
    searchTeacherStudents(query, 10)
      .then((studentsResponse) => {
        if (cancelled) return;
        const rows = Array.isArray(studentsResponse?.students)
          ? studentsResponse.students
          : Array.isArray(studentsResponse)
            ? studentsResponse
            : [];
        const currentClassIds = new Set(
          affectedOptions.filter((row) => row.type === "class").map((row) => String(row.rawId))
        );
        const currentSchoolRows = rows
          .filter((student) => {
            const studentSchoolId = student?.schoolid || student?.schoolId || student?.school_id || student?.school?.id;
            const studentClassId = student?.classid || student?.classId || student?.class_id || student?.class?.id || student?.class?._id;
            if (studentSchoolId) return String(studentSchoolId) === String(schoolid);
            return Boolean(studentClassId && currentClassIds.has(String(studentClassId)));
          })
          .map((student) => normaliseStudent(student, countryGrades));
        setStudentOptions(currentSchoolRows);
        const prefilledStudent = currentSchoolRows.find((row) =>
          (prefill?.studentId && String(row.rawId) === String(prefill.studentId)) ||
          (prefill?.studentName && normaliseText(row.name) === normaliseText(prefill.studentName))
        );
        if (prefilledStudent) setSelectedAffectedId(prefilledStudent.id);
      })
      .catch(() => {
        if (!cancelled) setStudentOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearchingStudents(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, affectedSearch, affectedOptions, countryGrades, prefill, schoolid]);

  const canContinue = Boolean(
    issueType.trim() && deviceType.trim() && numberAffected.trim() && description.trim()
  );
  const affectedResults = [...studentOptions, ...affectedOptions].filter((row) =>
    !affectedSearch.trim() ||
    [row.name, row.helper].some((value) =>
      normaliseText(value).toLowerCase().includes(normaliseText(affectedSearch).toLowerCase())
    )
  );
  const selectAffectedRow = (row) => {
    setSelectedAffectedId(row.id);
    setAffectedSearch(row.name);
  };
  const selectedAffected = [...studentOptions, ...affectedOptions].find((row) => row.id === selectedAffectedId);

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Technical issue escalated."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const sendTechnicalIssue = async () => {
    if (!canContinue || isSubmitting) return;
    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const targetType = selectedAffected?.type === "child" ? "student" : selectedAffected?.type;
    const target = selectedAffected?.rawId && !isPlaceholderWorkflowId(selectedAffected.rawId)
      ? {
          type: targetType,
          [`${targetType}Id`]: selectedAffected.rawId
        }
      : {};

    const payload = buildTeacherSupportRequestPayload({
      type: "technical_support",
      schoolId: schoolid,
      target,
      details: {
        issueType,
        affectedName: selectedAffected?.name || affectedSearch,
        affectedType: selectedAffected?.type,
        deviceType,
        browser,
        operatingSystem,
        numberAffected,
        description
      },
      source: {
        workflow: "escalate-technical-issue"
      }
    });

    try {
      const response = await createTeacherSupportRequest(payload);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-escalate-technical-issue-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Contact Support</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="technical-issue-intro">Tell us what is broken and add the device details if you know them.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="technical-issue-section">
              <Form.Label>What kind of issue is it?</Form.Label>
              <div className="technical-issue-option-list">
                {issueTypes.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`technical-issue-option${issueType === option.value ? " selected" : ""}`}
                    onClick={() => setIssueType(option.value)}
                    aria-pressed={issueType === option.value}
                  >
                    <span className="technical-issue-radio" aria-hidden="true" />
                    <span>
                      <strong>{option.value}</strong>
                      <span>{option.helper}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="technical-issue-section" controlId="technicalIssueAffectedSearch">
              <Form.Label>Affected record (optional)</Form.Label>
              <Form.Text as="div" className="technical-issue-field-label">
                Search child, class, or teacher
              </Form.Text>
              <Form.Control
                type="search"
                value={affectedSearch}
                onChange={(event) => {
                  setAffectedSearch(event.target.value);
                  setSelectedAffectedId("");
                }}
                aria-label="Affected class search"
              />
              <Form.Text className="text-muted">
                Add the affected class, child, or teacher if known.
              </Form.Text>
            </Form.Group>

            <div className="technical-issue-results" aria-label="Affected search results">
              {(isLoadingAffected || isSearchingStudents) && (
                <div className="technical-issue-search-state">
                  <Spinner animation="border" size="sm" />
                  <span>Searching school records...</span>
                </div>
              )}
              {!isLoadingAffected && !isSearchingStudents && selectedAffectedId && (
                affectedResults
                  .filter((row) => row.id === selectedAffectedId)
                  .map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      className="technical-issue-result selected"
                      onClick={() => setSelectedAffectedId("")}
                      aria-label={`Selected ${row.name}`}
                      aria-pressed="true"
                    >
                      <span className="technical-issue-radio" aria-hidden="true" />
                      <span>
                        <strong>{row.name}</strong>
                        <span>{row.helper}</span>
                      </span>
                    </button>
                  ))
              )}
              {!isLoadingAffected && !isSearchingStudents && !selectedAffectedId && affectedResults.slice(0, 6).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="technical-issue-result"
                  onClick={() => selectAffectedRow(row)}
                  aria-label={`Select ${row.name}`}
                  aria-pressed="false"
                >
                  <span className="technical-issue-radio" aria-hidden="true" />
                  <span>
                    <strong>{row.name}</strong>
                    <span>{row.helper}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="technical-issue-section">
              <Form.Label as="div">Required information</Form.Label>
              <Form.Group className="mb-3" controlId="technicalIssueDevice">
                <Form.Label className="technical-issue-field-label">Device type</Form.Label>
                <Form.Control value={deviceType} onChange={(event) => setDeviceType(event.target.value)} />
              </Form.Group>
              <Form.Group controlId="technicalIssueCount">
                <Form.Label className="technical-issue-field-label">Number affected</Form.Label>
                <Form.Control value={numberAffected} onChange={(event) => setNumberAffected(event.target.value)} />
              </Form.Group>
            </div>

            <Form.Group className="technical-issue-section" controlId="technicalIssueDescription">
              <Form.Label>Short description</Form.Label>
              <Form.Text as="div" className="technical-issue-field-label">What happened?</Form.Text>
              <Form.Control
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                aria-label="Short description"
              />
            </Form.Group>

            <div className="technical-issue-section">
              <Form.Label as="div">Useful information, if available</Form.Label>
              <Form.Group className="mb-3" controlId="technicalIssueBrowser">
                <Form.Label className="technical-issue-field-label">Browser (optional)</Form.Label>
                <Form.Control value={browser} onChange={(event) => setBrowser(event.target.value)} />
              </Form.Group>
              <Form.Group controlId="technicalIssueOs">
                <Form.Label className="technical-issue-field-label">Operating system (optional)</Form.Label>
                <Form.Control value={operatingSystem} onChange={(event) => setOperatingSystem(event.target.value)} />
                <Form.Text className="text-muted">On a tablet or iPad, this can be found in Settings.</Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="technical-issue-footer-note">{canContinue ? "Issue details captured." : "Complete the issue type, device, number affected and description."}</span>
            {onBackToTrialAccess && (
              <Button variant="outline-secondary" onClick={onBackToTrialAccess}>Back to Trial Access Help</Button>
            )}
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setStage("checks")} disabled={!canContinue}>Next</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "checks" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Quick checks</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="technical-issue-intro">If there is an issue during screening:</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="technical-issue-warning">
              <ul>
                <li>Hard refresh the page.</li>
                <li>Close other tabs.</li>
                <li>Fully restart the device to clear temporary data.</li>
              </ul>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="technical-issue-footer-note">Quick checks shown.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="primary" onClick={sendTechnicalIssue} disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send issue"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Issue Escalated</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="technical-issue-intro">Support will review it and let you know the next steps.</p>
            <Alert variant="success" className="technical-issue-success">
              <span className="technical-issue-ok-badge" aria-hidden="true">OK</span>
              <span>
                <strong>
                  {isDuplicatePending
                    ? "Your technical issue is already in progress."
                    : "Your technical issue has been escalated."}
                </strong>
                <span>
                  {supportReference ? `Reference: ${supportReference}. ` : ""}
                  Support will review it and let you know the next steps.
                </span>
              </span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="technical-issue-footer-note">Task complete.</span>
            <Button variant="primary" onClick={handleDone}>Close</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherEscalateTechnicalIssueModal;
